/**
 * What these helpers actually need from a theme: the mode, and the brand ramp.
 *
 * STRUCTURAL ON PURPOSE, rather than MUI's `Theme`. This monorepo resolves `@mui/material`
 * twice — once under `react@18.3.1` for `apps/*` and once under `react@19.1.0` for
 * `packages/ui` — so the two `Theme` types are nominally distinct and passing an app's theme
 * into a `packages/ui` helper is a type error even though the value is identical at runtime.
 * That is a pre-existing repo condition, not a new one: 61 instances of it already sit in the
 * platform typecheck (`DatePickerField`, `SidebarList`, `GlossaryPage`, `LayoutOptions`, …).
 *
 * A structural parameter sidesteps it — both copies satisfy this shape — so a NEW helper does
 * not add to that pile. It is also honestly narrower: these functions read `palette.mode` and
 * `palette.brand` and nothing else, and saying so is more accurate than demanding a whole
 * `Theme`. The duplicate-instance problem itself is worth fixing at the dependency graph, but
 * that is not this change.
 */
export type BrandThemeLike = {
  palette: { mode: "light" | "dark" } & Record<string, any>;
};

/**
 * The one source for the brand PURPLE GRADIENT — the tab indicator's bar, and the stroke
 * of a selected nav icon.
 *
 * ## Why this exists
 *
 * The gradient was written inline in `Tabs.tsx` as a hardcoded
 * `linear-gradient(90deg, rgba(130,90,195,1) 0%, rgb(55,39,137) 100%)`, with
 * `theme.palette.brand[600]` commented out on the line above it. Note what that comment
 * records: someone reached for the token, found it did not express a gradient, and
 * hardcoded two RGB triples instead. The moment a SECOND surface wants the same gradient,
 * those triples get copied — and a copied colour is a colour that drifts, silently,
 * because nothing compares one file's literal to another's (§36, and the same failure
 * `fieldChrome.ts` and `navChrome.ts` were each created to stop).
 *
 * So the stops live here and both consumers read them.
 *
 * ## The stops are NOT palette tokens, and that is deliberate
 *
 * `#825AC3` is `rgba(130,90,195,1)` and `#372789` is `rgb(55,39,137)` — the shipped values,
 * transcribed to hex because an SVG `<stop stop-color>` takes one colour, not a CSS gradient.
 * **Check that transcription arithmetically, not by eye.** The first draft of this file wrote
 * `#825ABF`, which is (130,90,**191**) — four off in blue, and a purple nobody would question
 * standing next to the real one. A silent colour drift introduced by the very commit whose
 * whole purpose was to stop the colour drifting. `195 = 0xC3`, not `0xBF`.
 *
 * They are close to `brand[500]`/`brand[900]` but equal to neither
 * (`brand[500]` is `#8F68EA`, `brand[900]` is `#4D278B` — both more saturated). Snapping
 * them to the ramp would change the tab indicator that is already shipped and approved,
 * for a tidiness that no one asked for. They are recorded here as the canonical brand
 * gradient rather than re-derived.
 *
 * ## BACKGROUND vs FOREGROUND — the split this file exists to enforce
 *
 * Exactly the distinction `brandText.ts` draws, and for the same measured reason.
 *
 * As a BACKGROUND (the tab indicator: a solid 2px bar) the gradient is the content. Its
 * own colour is what you are looking at, it sits on a light and a dark surface equally
 * happily, and it is mode-INDEPENDENT.
 *
 * As a FOREGROUND (the stroke of a 1px-weight icon) it is ink, and ink is a relationship
 * with the surface under it. Measured on the SELECTED nav row — the tinted surface the
 * icon actually sits on, `brand[500]` at 12% over `#fafafa` in light and at 20% over
 * `#18181b` in dark:
 *
 *                        light (237,232,248)      dark (48,40,68)
 *   #825AC3  (start)          4.17:1                 2.78:1
 *   #372789  (end)            9.65:1                 1.20:1
 *
 * **1.20:1 is invisible.** Painting the shipped gradient onto a dark-mode icon would fade
 * the right-hand half of every selected glyph into its own row — the exact inversion the
 * `ToggleButton` track bug is the standing example of, where a colour chosen in one mode
 * silently evaporates in the other.
 *
 * Dark therefore takes a LIFTED PAIR off the same ramp — `brand[300]` -> `brand[500]`,
 * measured 7.85:1 -> 3.53:1 on that surface. Both ends clear the 3:1 that WCAG 1.4.11
 * asks of a non-text graphic, and the gesture is preserved: a violet that DEEPENS from
 * left to right, in both modes. What changes between modes is only where on the ramp that
 * journey starts, which is precisely `brandText`'s resolution for the same problem.
 *
 * (The perceptual span is comparable too, which is why the dark pair is not a token
 * lighter still: light traverses a 2.3x contrast range, dark 2.2x. `brand[300]` ->
 * `brand[400]` would be safer on paper — 7.85 -> 5.43 — and too small a step to read as a
 * gradient at all, which would spend the whole mechanism to render a flat colour.)
 */

/**
 * `[start, end]` of the canonical brand gradient — DARK to LIGHT, left to right.
 *
 * ## The direction is a decision, taken 2026-10-29, and it reversed what shipped
 *
 * These two colours are the shipped tab-indicator values and have not changed. Their
 * ORDER has: the gradient ran `#825AC3 -> #372789` (light to dark) everywhere a bar
 * used it, while `@repo/ui`'s `Switch` ran `#0B0A67 -> #7850B9` (dark to light) — so the
 * system said both, and which one you met depended on which control you were looking at.
 * Spencer called it: the brand gradient runs dark on the left, light on the right.
 *
 * The case for the old direction was real and is recorded here rather than deleted,
 * because it is the thing to re-read if this is ever revisited: on a PROGRESS bar,
 * light-to-dark puts the heaviest colour at the filled end, and a 17%-full bar then shows
 * only its light end, which reads as "early". Dark-to-light starts a nearly-empty bar at
 * its most emphatic point. That argument lost to consistency with the rest of the brand,
 * which is the right trade — a design system that contradicts itself costs more than a
 * progress bar that reads slightly hot at 17%.
 *
 * ## This flips an SVG STROKE too, not just bars
 *
 * `brandGradientStops` paints the selected nav icon. Flipping the pair flips which end of
 * that stroke is dark. That is intended — leaving the icon on the old direction would
 * reintroduce exactly the split this change exists to close — but it is a visible change
 * to a shipped surface, so it is stated rather than discovered.
 */
export const BRAND_GRADIENT: readonly [string, string] = ["#372789", "#825AC3"];

/**
 * The dark-mode FOREGROUND pair — `brand[500]` -> `brand[300]`. See the header.
 *
 * Ordered dark-to-light to match `BRAND_GRADIENT` above. It was `brand[300] -> brand[500]`;
 * flipping the canonical pair without flipping this one would have made the stroke run one
 * way in light mode and the other way in dark, which is the same contradiction one level
 * down.
 */
const BRAND_GRADIENT_DARK_FG: readonly [string, string] = ["#8F68EA", "#C7BBF7"];

/**
 * The gradient as a CSS value, for BACKGROUND use (the tab indicator).
 *
 * Mode-independent on purpose — see the header's background/foreground split. If you are
 * reaching for this to colour something's TEXT or STROKE, you want
 * `brandGradientStops(theme)` instead.
 */
export const brandGradientCss = (angle = "90deg"): string =>
  `linear-gradient(${angle}, ${BRAND_GRADIENT[0]} 0%, ${BRAND_GRADIENT[1]} 100%)`;

/**
 * The gradient's stops for FOREGROUND use — an SVG `<linearGradient>` painting a stroke.
 * Mode-aware: light keeps the canonical pair, dark takes the lifted one.
 *
 * Returns hex strings rather than a CSS gradient because a CSS `linear-gradient` cannot
 * paint an SVG stroke at all — a stroke needs a paint server (`stroke="url(#id)"`), which
 * needs the stops individually.
 */
export const brandGradientStops = (theme: BrandThemeLike): readonly [string, string] => {
  const brand = (theme.palette as any).brand;
  return theme.palette.mode === "light"
    ? BRAND_GRADIENT
    : [brand?.[500] || BRAND_GRADIENT_DARK_FG[0], brand?.[300] || BRAND_GRADIENT_DARK_FG[1]];
};
